import { rem } from 'polished'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import Lightbox from 'react-image-lightbox'
import { useScrollbarWidth } from 'react-use'
import styled from 'styled-components'
import { Thumbs, A11y } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import { PUBLIC_LAYOUT_SIDE_PADDING } from '@public/components/PublicLayout'
import { CardCarousel } from 'components/CardCarousel'
import { Image, IMAGE_LAYOUT } from 'components/Image'
import { Box, CONTAINER_HORIZONTAL_PADDING, Grid } from 'components/Layout'
import { ASPECT_RATIO } from 'components/UploadImage'
import { useLightbox } from 'hooks'
import { DEVICE, mq, PAGE_MAX_WIDTH, SPACE } from 'Theme'
import { useMedia } from 'useMedia'
import { ProductSwiperFadeBox } from './ProductSwiperFadeBox'
import { ProductThumbnailImage } from './ProductThumbnailImage'
import 'swiper/css/bundle'

const THUMBNAIL_WIDTH = rem(60)
const GALLERY_GRID_GAP = SPACE.PX_20

// This negates the padding used in PublicLayout on mobile.
const MainSwiperWrapper = styled(Box)`
  margin: 0 -${CONTAINER_HORIZONTAL_PADDING};
  @media screen and (max-width: ${DEVICE.TABLET}) and (min-width: ${PAGE_MAX_WIDTH.PUBLIC_LAYOUT_MOBILE}) {
    margin: 0 -${PUBLIC_LAYOUT_SIDE_PADDING};
  }
  ${mq.from.TABLET`
    margin: 0;
  `}
  cursor: zoom-in;
  user-select: none;
`

const ThumbnailSwiper = styled(Swiper)`
  width: ${THUMBNAIL_WIDTH};
  // Height of thumbnail swiper must be set via css for it to work properly. Height should be equal to the height of the main swiper.
  max-height: ${({ thumbnailSwiperHeightFormula, scrollbarWidth }) =>
    `calc(${
      thumbnailSwiperHeightFormula.MOBILE
    } - ${THUMBNAIL_WIDTH} - ${GALLERY_GRID_GAP} - ${rem(scrollbarWidth)} * ${
      thumbnailSwiperHeightFormula.ratio
    })`};

  @media screen and (min-width: ${PAGE_MAX_WIDTH.LAYOUT}) {
    max-height: ${({ thumbnailSwiperHeightFormula }) =>
      `calc(${thumbnailSwiperHeightFormula.DESKTOP} - ${THUMBNAIL_WIDTH} - ${GALLERY_GRID_GAP})`};
  }

  .swiper-slide {
    flex-basis: fit-content;
  }
`

export const ProductImageGallery = ({
  images = [],
  thumbnailSwiperHeightFormula,
}) => {
  const [mainSwiper, setMainSwiper] = useState(null)
  const [thumbsSwiper, setThumbsSwiper] = useState(null)
  const [activeImageIndex, setActiveImageIndex] = useState(0)
  const [isBeginning, setIsBeginning] = useState(true)
  const [isEnd, setIsEnd] = useState(false)
  const scrollbarWidth = useScrollbarWidth()
  const { isLightboxOpen, openLightbox, closeLightbox, lightboxRef } =
    useLightbox()
  const { MOBILE: isMobile } = useMedia()

  const imagesLength = images.length

  useEffect(() => {
    if (mainSwiper && imagesLength > 0) {
      // recalculate size of swiper when scrollbar visibility changes
      mainSwiper.update()
    }
  }, [imagesLength, isLightboxOpen, mainSwiper])

  if (imagesLength === 0) {
    return null
  }

  return (
    <Grid
      gridTemplateColumns={{ MOBILE: '1fr', TABLET: `${THUMBNAIL_WIDTH} 1fr` }}
      gridGap={GALLERY_GRID_GAP}
    >
      {!isMobile && (
        <ProductSwiperFadeBox
          isBeginning={isBeginning}
          isEnd={isEnd}
          position="relative"
        >
          <ThumbnailSwiper
            modules={[Thumbs, A11y]}
            slidesPerView="auto"
            centeredSlides
            centeredSlidesBounds
            watchSlidesProgress
            watchOverflow
            direction="vertical"
            thumbnailSwiperHeightFormula={thumbnailSwiperHeightFormula}
            scrollbarWidth={scrollbarWidth ?? 0}
            allowTouchMove={false}
            observer
            observeSlideChildren
            onSwiper={(currentSwiperInstance) => {
              setThumbsSwiper(currentSwiperInstance)
              setIsBeginning(currentSwiperInstance.isBeginning)
              setIsEnd(imagesLength === 1 || currentSwiperInstance.isEnd)
            }}
            onSlideChange={(currentSwiperInstance) => {
              setIsBeginning(currentSwiperInstance.isBeginning)
              setIsEnd(imagesLength === 1 || currentSwiperInstance.isEnd)
            }}
          >
            {images.map(({ url, alt, id }, index) => (
              <SwiperSlide key={`thumbnail-${id}-${index}`}>
                <ProductThumbnailImage
                  swiper={mainSwiper}
                  url={url}
                  alt={alt}
                  index={index}
                  isActive={activeImageIndex === index}
                />
              </SwiperSlide>
            ))}
          </ThumbnailSwiper>
        </ProductSwiperFadeBox>
      )}

      <MainSwiperWrapper overflow="hidden">
        <CardCarousel
          modules={!isMobile ? [Thumbs] : []}
          isFullWidth
          isWithPagination
          rewind
          thumbs={
            !isMobile ? { swiper: thumbsSwiper, autoScrollOffset: 3 } : null
          }
          setSwiper={setMainSwiper}
          swiper={mainSwiper}
          slidesPerView={{ MOBILE: 1, DESKTOP: 1 }}
          offset={{ MOBILE: 0, DESKTOP: 0 }}
          spaceBetween={{ MOBILE: 0, DESKTOP: 0.5 }}
          onSlideChange={(currentSwiperInstance) => {
            // Used to prevent the side effect of autoScrollOffset on the thumbnail swiper fade box
            const currentSlideIndex = currentSwiperInstance.realIndex
            setActiveImageIndex(currentSlideIndex)
            if (currentSlideIndex === imagesLength - 1) {
              setIsEnd(true)
            }
          }}
          observeSlideChildren
          onObserverUpdate={() => {
            // This is triggered when number of images changes, e.g. when changing variants
            mainSwiper.slideTo(0)
            setActiveImageIndex(0)
          }}
          cards={images.map(({ url, alt, id }, index) => ({
            key: id,
            card: (
              <Image
                aspectRatio={ASPECT_RATIO.PRODUCTS}
                alt={alt || `Product image #${index}`}
                layout={IMAGE_LAYOUT.RESPONSIVE}
                objectFit="cover"
                src={url}
                onClick={openLightbox}
                sizes="(max-width: 768px) 100vw, (max-width: 1920px) 40vw, 25vw"
              />
            ),
          }))}
          hasArrowsCentered
        />
      </MainSwiperWrapper>
      {isLightboxOpen && (
        <Lightbox
          ref={lightboxRef}
          mainSrc={images[activeImageIndex]?.url}
          prevSrc={
            images[(activeImageIndex + images.length - 1) % images.length]?.url
          }
          nextSrc={images[(activeImageIndex + 1) % images.length]?.url}
          onCloseRequest={closeLightbox}
          onMovePrevRequest={() => {
            if (mainSwiper) {
              mainSwiper.slideToLoop(
                (activeImageIndex + images.length - 1) % images.length
              )
            }
          }}
          onMoveNextRequest={() => {
            if (mainSwiper) {
              mainSwiper.slideToLoop(
                (activeImageIndex + images.length + 1) % images.length
              )
            }
          }}
        />
      )}
    </Grid>
  )
}

ProductImageGallery.propTypes = {
  images: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      url: PropTypes.string,
    })
  ).isRequired,
  thumbnailSwiperHeightFormula: PropTypes.shape({
    MOBILE: PropTypes.string.isRequired,
    DESKTOP: PropTypes.string.isRequired,
    ratio: PropTypes.number.isRequired,
  }).isRequired,
}
